import { useEffect, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import ButtonComponent from "../../../../components/ButtonComponent";
import { InputGroup, Input, InputGroupText } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import {
  useLazyGetMenuListByRestaurantIdQuery,
} from "../../../../redux/services/setMenuService";
import { SetMenu } from "../../../../redux/setMenuSlice";
import { Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  useUpdateMenuItemAvailabilityMutation,
} from "../../../../redux/services/menuItemService";
import { toast } from "react-toastify";
import { formatTimestamp } from "../../../../utility/Utility";
import { DEFAULT_IMAGE_URL, SET_MENU } from "../../../../constants/constans";
import WeekdayCalendar, {
  daysOfWeek,
} from "../../../../components/WeekDayCalender";
import { setRowsPerPage } from "../../../../redux/configurationComboMenuSlice";
import TableLoadingComponent from "../../../../components/TableLoadingComponent";
import SavingButton from "../../../../components/SavingMessageComponent";


const ComboItemsAvailableDays: React.FC = () => {
  const dispatch = useDispatch();

  const restaurantId = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails.id
  );

  const rowsPerPage = useSelector((state: any) => state.configurationComboMenu.rowsPerPage);

  const [setMenus, setSetMenus] = useState<SetMenu[]>([]);
  const [selectAllDays, setSelectAllDays] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredItems, setFilteredItems] = useState<SetMenu[]>([]);

  const [trigger, { data: menuData, isLoading }] =
    useLazyGetMenuListByRestaurantIdQuery();
  const [updateMenuItemAvailability] = useUpdateMenuItemAvailabilityMutation();

  useEffect(() => {
    if (restaurantId) {
      trigger(restaurantId);
    }
    if (menuData?.setMenuList) {
      setSetMenus(menuData?.setMenuList);
    }
  }, [restaurantId, trigger, menuData]);

  // Filter items based on search term (flexible search - words can be in any order)
  useEffect(() => {
    if (setMenus && setMenus.length > 0) {
      if (searchTerm.trim() === "") {
        setFilteredItems(setMenus);
      } else {
        const searchWords = searchTerm.toLowerCase().trim().split(/\s+/);
        const filtered = setMenus.filter((item: SetMenu) => {
          const searchableText = [
            item.displayName || "",
            (item as any).productId || "",
          ].join(" ").toLowerCase();
          return searchWords.every(word => searchableText.includes(word));
        });
        setFilteredItems(filtered);
      }
    }
  }, [setMenus, searchTerm]);

  const handleSelectAllDays = () => {
    setSetMenus((prevItems) =>
      prevItems.map((item) => ({
        ...item,
        availableDays: selectAllDays
          ? []
          : Object.entries(daysOfWeek).map((value) => value[1]),
      }))
    );
    setSelectAllDays(!selectAllDays);
    setHasChanges(true);
  };

  const handleSaveChanges = async () => {
    setIsSaving(true);
    const menuItemsData = setMenus.map((item) => ({
      itemId: item.id,
      availableDays: item.availableDays,
    }));
    const requestData = { restaurantId, menuType: SET_MENU, menuItemsData };

    try {
      await updateMenuItemAvailability(requestData).unwrap();
      toast.success("Availability updated successfully!");
      setHasChanges(false);
    } catch (error) {
      toast.error("Failed to update availability.");
    } finally {
      setIsSaving(false);
    }
  };

  const setAvailableDays = (id: string, days: number[]) => {
    setSetMenus((prevItems) =>
      prevItems.map((item) =>
        item.id === id ? { ...item, availableDays: days } : item
      )
    );
    setHasChanges(true);
  };

  useEffect(() => {
    const allDaysSelected = setMenus.every(
      (item) => item.availableDays?.length === Object.keys(daysOfWeek).length
    );
    setSelectAllDays(allDaysSelected);
  }, [setMenus]);

  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    dispatch(setRowsPerPage(newRowsPerPage));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const columns: TableColumn<SetMenu>[] = [
    {
      name: <h6 className="fw-bold">Image</h6>,
      cell: (row: SetMenu) => (
        <img
          src={row.imageUrl || DEFAULT_IMAGE_URL}
          alt=""
          className="set-menu-image"
        />
      ),
    },
    {
      name: <h6 className="fw-bold">Name</h6>,
      selector: (row: SetMenu) => row.displayName,
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">Price(Rs.)</h6>,
      selector: (row: SetMenu) => row.price,
    },
    {
      name: <h6 className="fw-bold">Created Date</h6>,
      selector: (row: SetMenu) => formatTimestamp(row.createdAt),
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">Availability</h6>,
      cell: (row) => (
        <div className="menuitem-availability">
          <WeekdayCalendar
            availableDays={row.availableDays}
            setAvailableDays={(days) => setAvailableDays(row.id, days)}
          />
        </div>
      ),
    },
  ];

  return (
    <>
      {isLoading ? (
        <div className="bg-white">
          <TableLoadingComponent numColumns={5} />
        </div>
      ) : (
        <>
          <div className="menuitem-save-change mt-2 pb-2 d-flex align-items-center">
            <Form.Check
              type="checkbox"
              label={selectAllDays ? "Deselect All" : "Select All"}
              checked={selectAllDays}
              onChange={handleSelectAllDays}
              style={{ paddingLeft: '3%' }}
              className="me-3"
            />
            <div>
              {isSaving ? (<SavingButton isSaving={true} />
              ) : (<ButtonComponent
                titel={"Save"}
                onClick={handleSaveChanges}
                isDisabled={false}
              />)}
              {hasChanges && (
                <div className="mt-1 text-warning">
                  Save changes
                </div>
              )}
            </div>
          </div>

          {/* Search Input */}
          <div className="p-3 bg-white">
            <InputGroup>
              <InputGroupText>
                <FontAwesomeIcon icon={faSearch} />
              </InputGroupText>
              <Input
                type="text"
                placeholder="Search menu items by name or SKU ID..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="search-input"
              />
            </InputGroup>
          </div>

          <DataTable
            columns={columns}
            data={filteredItems}
            pagination
            highlightOnHover
            paginationPerPage={rowsPerPage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            noDataComponent={
              <div className="py-4 text-center">
                {searchTerm ? `No menu items found for "${searchTerm}"` : "No menu items available"}
              </div>
            }
          />
        </>
      )}
    </>
  );
};

export default ComboItemsAvailableDays;
